import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import * as merge from 'deepmerge';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { assetUrl } from './asset-url';

interface ILangSwitchedTranslateHttpLoaderOptions {
  prefix?: string;
  suffix?: string;
  resources?: string[];
  langCodeMapping?: Map<string, string>;
}
/**
 * This class maps language codes and loads the appropriate translation resource.
 */
export class LangSwitchedTranslateHttpLoader implements TranslateLoader {
  private readonly prefix: string;
  private readonly suffix: string;
  private readonly resources: string[];
  private readonly langCodeMapping: Map<string, string>;

  constructor(
    private readonly http: HttpClient,
    {
      prefix = `${assetUrl()}/i18n/`,
      suffix = '.json',
      resources = ['v3'],
      langCodeMapping = new Map([
        ['nn', 'no'],
        ['nb', 'no'],
        ['en', 'en']
      ])
    }: ILangSwitchedTranslateHttpLoaderOptions = {}
  ) {
    this.prefix = prefix;
    this.suffix = suffix;
    this.resources = resources;
    this.langCodeMapping = langCodeMapping;
  }

  public getTranslation(lang: string): Observable<object> {
    const languageCode = this.updateLanguageCode(lang);

    return forkJoin(this.fetchTranslations(languageCode)).pipe(
      map((responses) => merge.all(responses))
    );
  }

  /** Maps language codes to supported language codes */
  private updateLanguageCode(language: string): string {
    let output = language;

    this.langCodeMapping.forEach((value, key) => {
      if (language.startsWith(key)) {
        output = value;
      }
    });

    return output;
  }

  /** Create console error if translation file could not be located */
  private handleTranslationFailure(path: string): () => Observable<object> {
    return () => {
      console.error(`Could not find translation file: ${path}`);

      return of({});
    };
  }

  /** Fetch matching language file from each resource director */
  private fetchTranslations(language: string): Observable<object>[] {
    return this.resources.map((resource) => {
      const path = this.getPath(language, resource);

      return this.http.get(path).pipe(catchError(this.handleTranslationFailure(path)));
    });
  }

  /** Create language resource path string */
  private getPath(language: string, resource: string): string {
    return `${this.prefix}${resource}/${language}${this.suffix}`;
  }
}
